import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Download, FileSpreadsheet, Printer } from 'lucide-react';
import { ReactSortable } from 'react-sortablejs';
import Cookies from 'universal-cookie';
import { Skeleton } from '../../../../../components/shared/Skeleton/Skeleton';
import { listAccountingSchedules } from '../../../../../services/FixedAssetsService';
import { ChartAccount, listChartOfAccounts } from '../../../../../services/ChartOfAccountsService';
import { selectActiveCompany } from '../../../../../redux/slices/activeCompanySlice';
import { AccountingSchedule } from '../../../../../types/FixedAsset.types';
import { formatEuro } from './financeMasters';
import { formatDate } from '../forms/FormParts';
import { printElement } from '../printElement';
import { applyOrder } from '../financeNav';
import '../../shared-modules.css';
import './FinanceReports.css';

const REPORTS = [
  { id: 'pnl', name: 'Cuenta de Pérdidas y Ganancias (P&L)', desc: 'Estructura analítica de ingresos, costes y margen neto' },
  { id: 'balance', name: 'Balance de Sumas y Saldos', desc: 'Cuadrante general de comprobación del PGC' },
  { id: 'mayor', name: 'Extracto Analítico del Libro Mayor', desc: 'Saldos acumulados por cuenta contable' },
  { id: 'ventas_cliente', name: 'Informe de Ventas por Cliente', desc: 'Distribución de facturación y cuota de clientes' },
  { id: 'gastos_cat', name: 'Informe de Gastos por Categoría', desc: 'Desglose de compras, suministros y servicios' },
  { id: 'fiscal', name: 'Resumen de Liquidación Fiscal', desc: 'Acumulado de IVA repercutido, soportado e IRPF' },
  { id: 'cashflow', name: 'Proyección de Flujo de Caja', desc: 'Estimación de liquidez y tesorería a 12 meses' },
  { id: 'amortizaciones', name: 'Informe de amortizaciones', desc: 'Valores de adquisición y amortización del inmovilizado por cuenta de mayor' }
];

const COMPANY = { name: 'ALBATROSS EDITORIAL S.A.', meta: 'NIF: A88243510 • PLAN GENERAL CONTABLE 2026' };

// Reparto por cuentas del P&L: los totales son reales, el desglose es estimado
const INCOME_SPLIT = [
  { code: '700000', name: 'Ventas de libros y mercaderías editoriales', share: 0.75 },
  { code: '705000', name: 'Prestación de servicios de edición y distribución', share: 0.25 }
];

const EXPENSE_SPLIT = [
  { code: '600000', name: 'Compras de papel e insumos de impresión', share: 0.45 },
  { code: '628000', name: 'Suministros (Electricidad, red e infraestructura)', share: 0.25 },
  { code: '629000', name: 'Otros servicios y honorarios profesionales', share: 0.3 }
];

type RowKind = 'total' | 'line' | 'result';

interface PnlRow {
  kind: RowKind;
  code: string;
  name: string;
  amount: number;
  pct: string;
  tone?: 'income' | 'expense';
}

interface AmortRow {
  accountCode: string;
  accountName: string;
  acquisition: number;
  depreciation: number;
}

// Una fila por cuenta de mayor. Las cuotas son mensuales: se comparan por año-mes
const buildAmortRows = (schedules: AccountingSchedule[], accounts: ChartAccount[], to: string): AmortRow[] => {
  const toMonth = to.slice(0, 7);
  const byAccount: Record<string, AmortRow> = {};
  schedules.forEach(({ asset, valuation, entries }) => {
    if (!valuation || asset.acquisitionDate > to) return;
    const key = asset.assetAccountId;
    if (!byAccount[key]) {
      const account = accounts.filter(a => a.id === key)[0];
      byAccount[key] = {
        accountCode: account ? account.code : '',
        accountName: account ? account.name : asset.assetAccountName || 'Sin cuenta de mayor',
        acquisition: 0,
        depreciation: 0
      };
    }
    byAccount[key].acquisition += valuation.acquisitionValue;
    // Lo contabilizado, como la última columna de Contabilizar activos (en negativo, como SAP)
    entries
      .filter(e => e.status === 'posted' && `${e.fiscalYear}-${('0' + e.period).slice(-2)}` <= toMonth)
      .forEach(e => { byAccount[key].depreciation -= e.plannedAmount; });
  });
  return Object.keys(byAccount)
    .map(key => byAccount[key])
    .sort((a, b) => a.accountCode.localeCompare(b.accountCode));
};

const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Excel abre una tabla HTML guardada como .xls
const downloadXls = (fileName: string, title: string, headers: string[], rows: (string | number)[][], companyName = COMPANY.name) => {
  const cell = (v: string | number) =>
    typeof v === 'number'
      ? `<td style="mso-number-format:'#,##0.00'">${(Math.round((v + Number.EPSILON) * 100) / 100).toFixed(2)}</td>`
      : `<td>${escapeHtml(v)}</td>`;
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="UTF-8"></head>
<body>
<table>
<tr><td colspan="${headers.length}"><b>${escapeHtml(companyName)}</b></td></tr>
<tr><td colspan="${headers.length}">${escapeHtml(title)} — ${new Date().toLocaleDateString('es-ES')}</td></tr>
<tr></tr>
<tr>${headers.map(h => `<th>${escapeHtml(h)}</th>`).join('')}</tr>
${rows.map(r => `<tr>${r.map(cell).join('')}</tr>`).join('\n')}
</table>
</body>
</html>`;
  const blob = new Blob(['﻿', html], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

interface FinanceReportsCenterViewProps {
  privacyMode: boolean;
  totalIncome: number;
  totalExpense: number;
  // Abre solo este informe, sin la barra para elegir los demás
  onlyReport?: string;
}

export const FinanceReportsCenterView = ({ privacyMode, totalIncome, totalExpense, onlyReport }: FinanceReportsCenterViewProps) => {
  // Id del cookie de login: está disponible al instante, sin esperar a /me.
  const [userId] = useState<string | number | undefined>(() => {
    try {
      return new Cookies().get('101Obex')?.user?.id;
    } catch {
      return undefined;
    }
  });
  const [orderedReports, setOrderedReports] = useState(() => {
    try {
      const saved = userId ? JSON.parse(localStorage.getItem(`finances-reports-order-${userId}`) || '[]') : [];
      return applyOrder(REPORTS, saved);
    } catch {
      return REPORTS;
    }
  });

  const saveReportsOrder = (items: typeof REPORTS) => {
    setOrderedReports(items);
    if (!userId) return;
    try {
      localStorage.setItem(`finances-reports-order-${userId}`, JSON.stringify(items.map(r => r.id)));
    } catch {
      // Sin almacenamiento disponible el orden solo dura la sesión.
    }
  };

  const [selectedReport, setSelectedReport] = useState(() => onlyReport ?? orderedReports[0].id);

  const paperRef = useRef<HTMLDivElement>(null);

  // Informe de amortizaciones: se carga la primera vez que se abre y al
  // cambiar las fechas solo se recalcula
  const year = new Date().getFullYear();
  const [amortFrom, setAmortFrom] = useState(`${year}-01-01`);
  const [amortTo, setAmortTo] = useState(`${year}-12-31`);
  const company = useSelector(selectActiveCompany);
  const [schedules, setSchedules] = useState<AccountingSchedule[] | null>(null);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);
  const [amortError, setAmortError] = useState('');
  const amortRequested = useRef(false);

  useEffect(() => {
    if (selectedReport !== 'amortizaciones' || amortRequested.current) return;
    amortRequested.current = true;
    // Sin sociedad, listAccountingSchedules traería los activos de todas
    if (!company) {
      setAmortError('No hay sociedad activa seleccionada.');
      setSchedules([]);
      return;
    }
    // Solo da el código de la cuenta: si falla, se muestra el nombre
    listChartOfAccounts().then(setAccounts).catch(e => console.error('Error fetching chart of accounts:', e));
    listAccountingSchedules({ companyId: company.id, status: 'active' })
      .then(setSchedules)
      .catch(err => {
        console.error('Error fetching depreciation schedules:', err);
        setAmortError(err instanceof Error ? err.message : 'No se pudieron cargar los activos fijos.');
        setSchedules([]);
      });
  }, [selectedReport]);

  const fmt = (val: number) => formatEuro(val, privacyMode);
  const pct = (val: number) => `${(totalIncome > 0 ? (val / totalIncome) * 100 : 0).toFixed(1)}%`;
  const netProfit = totalIncome - totalExpense;
  const report = REPORTS.find(r => r.id === selectedReport);

  const isAmort = selectedReport === 'amortizaciones';
  const amortLoading = !schedules;
  const amortRows = schedules ? buildAmortRows(schedules, accounts, amortTo) : [];
  const amortTotal: AmortRow = {
    accountCode: '',
    accountName: 'TOTAL',
    acquisition: amortRows.reduce((sum, r) => sum + r.acquisition, 0),
    depreciation: amortRows.reduce((sum, r) => sum + r.depreciation, 0)
  };
  const companyId = company ? company.id : '';
  // Sin €: la moneda va en su columna
  const amount = (val: number) =>
    privacyMode ? '•••••' : val.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Tres líneas por cuenta, como el listado de SAP.
  // Pendiente: ver cómo se calculan Val.valor adq., Correcciones, Amo prev., Val.Amo acumul. y Val.neto final
  const renderAmortBlock = (row: AmortRow, key: string, total = false) => (
    <React.Fragment key={key}>
      <tr className={total ? 'total' : ''}>
        <td rowSpan={3}>{companyId}</td>
        <td rowSpan={3}>{row.accountCode}</td>
        <td rowSpan={3}>{row.accountName}</td>
        <td className="right">{amount(row.acquisition)}</td>
        <td className="right">{amount(row.depreciation)}</td>
        <td className="right">{amount(row.acquisition + row.depreciation)}</td>
        <td rowSpan={3}>EUR</td>
      </tr>
      <tr className={total ? 'total' : ''}>
        <td className="right">—</td>
        <td className="right">—</td>
        <td />
      </tr>
      <tr className={total ? 'total block-end' : 'block-end'}>
        <td className="right">—</td>
        <td className="right">—</td>
        <td className="right">—</td>
      </tr>
    </React.Fragment>
  );

  const pnlRows: PnlRow[] = [
    { kind: 'total', code: 'GRUPO 7', name: 'IMPORTE NETO DE LA CIFRA DE NEGOCIOS (VENTAS)', amount: totalIncome, pct: '100.0%', tone: 'income' },
    ...INCOME_SPLIT.map(l => ({ kind: 'line' as RowKind, code: l.code, name: l.name, amount: totalIncome * l.share, pct: `${(l.share * 100).toFixed(1)}%` })),
    { kind: 'total', code: 'GRUPO 6', name: 'GASTOS DE EXPLOTACIÓN Y APROVISIONAMIENTOS', amount: totalExpense, pct: pct(totalExpense), tone: 'expense' },
    ...EXPENSE_SPLIT.map(l => ({ kind: 'line' as RowKind, code: l.code, name: l.name, amount: totalExpense * l.share, pct: '--' })),
    { kind: 'result', code: '', name: 'RESULTADO DEL EJERCICIO (BENEFICIO / PÉRDIDA):', amount: netProfit, pct: pct(netProfit), tone: netProfit >= 0 ? 'income' : 'expense' }
  ];

  const summaryRows: { label: string; value: number }[] = [
    { label: 'Total Facturación Acumulada', value: totalIncome },
    { label: 'Total Gastos Acumulados', value: totalExpense },
    { label: 'Resultado Operativo Neto', value: netProfit }
  ];

  const handlePrint = () => {
    if (paperRef.current) printElement(paperRef.current);
  };

  const handleDownloadXls = () => {
    const fileName = `${selectedReport}_${new Date().toISOString().split('T')[0]}.xls`;
    if (selectedReport === 'pnl') {
      downloadXls(
        fileName,
        report?.name || '',
        ['Cuenta PGC / Partida', 'Denominación', 'Importe (€)', '% s/ Venta'],
        pnlRows.map(r => [r.code, r.name, r.amount, r.pct])
      );
    } else if (selectedReport === 'amortizaciones') {
      // Las columnas pendientes de calcular van vacías
      downloadXls(
        fileName,
        `${report?.name || ''} (${formatDate(amortFrom)} - ${formatDate(amortTo)})`,
        ['Soc.', 'Cta. mayor', 'Denominación', 'Val.adq.acum.', 'Val.valor adq.', 'Correcciones', 'Amo ac.', 'Amo prev.', 'Val.Amo acumul.', 'Inicio val.cont.', 'Val.neto final', 'Mon.'],
        amortRows.concat(amortTotal).map(r => [
          companyId, r.accountCode, r.accountName,
          r.acquisition, '', '', r.depreciation, '', '', r.acquisition + r.depreciation, '', 'EUR'
        ]),
        company ? company.name : ''
      );
    } else {
      downloadXls(
        fileName,
        report?.name || '',
        ['Concepto', 'Importe (€)'],
        summaryRows.map(r => [r.label, r.value])
      );
    }
  };

  return (
    <div className="fin-reports">
      <div className="module-toolbar">
        <div className="fin-reports__heading">
          <FileSpreadsheet size={14} className="module-panel-title-icon" />
          <div>
            <h3 className="fin-reports__title">{onlyReport ? report?.name : 'Centro_De_Informes_Financieros_Y_Contables'}</h3>
            <p className="fin-reports__subtitle">{onlyReport ? report?.desc : 'Generación, visualización y exportación de estados financieros oficiales'}</p>
          </div>
        </div>
        <div className="fin-reports__actions">
          <button type="button" className="module-create-btn" onClick={handlePrint}>
            <Printer size={14} /> Imprimir / PDF
          </button>
          <button type="button" className="module-create-btn" onClick={handleDownloadXls}>
            <Download size={14} /> Descargar XLS
          </button>
        </div>
      </div>

      {!onlyReport && (
        <ReactSortable
          className="finances-category-nav"
          list={orderedReports}
          setList={saveReportsOrder}
          animation={150}
          forceFallback
          // Sin margen, 1px de temblor al pulsar se toma como arrastre y se pierde el clic.
          fallbackTolerance={15}
          scrollSensitivity={80}
          scrollSpeed={15}
          onStart={() => document.body.classList.add('sidebar-dragging')}
          onEnd={() => document.body.classList.remove('sidebar-dragging')}
        >
          {orderedReports.map(r => (
            <button
              key={r.id}
              type="button"
              title={r.desc}
              onClick={() => setSelectedReport(r.id)}
              className={selectedReport === r.id ? 'active' : ''}
            >
              {r.name}
            </button>
          ))}
        </ReactSortable>
      )}

      <div className="fin-report-paper" ref={paperRef}>
        <div className="fin-report-paper__header">
          <div>
            {!isAmort ? (
              <>
                <h2 className="fin-report-paper__company">{COMPANY.name}</h2>
                <p className="fin-report-paper__meta">{COMPANY.meta}</p>
              </>
            ) : company ? (
              <>
                <h2 className="fin-report-paper__company">{company.name}</h2>
                <p className="fin-report-paper__meta">NIF: {company.cif}</p>
              </>
            ) : amortLoading ? (
              <Skeleton width="220px" height="1.5rem" />
            ) : (
              <h2 className="fin-report-paper__company">—</h2>
            )}
          </div>
          <div className="fin-report-paper__info">
            <p className="fin-report-paper__report-name">{report?.name}</p>
            <p>Fecha de emisión: {new Date().toLocaleDateString('es-ES')}</p>
            {isAmort ? (
              <p>
                Ejercicio:{' '}
                <span className="fin-report-paper__screen-only">
                  <input type="date" className="fin-report-paper__date" value={amortFrom} onChange={(e) => e.target.value && setAmortFrom(e.target.value)} />
                  {' - '}
                  <input type="date" className="fin-report-paper__date" value={amortTo} onChange={(e) => e.target.value && setAmortTo(e.target.value)} />
                </span>
                <span className="fin-report-paper__print-only">{formatDate(amortFrom)} - {formatDate(amortTo)}</span>
              </p>
            ) : (
              <p>Ejercicio: 2026 (Moneda: EUR)</p>
            )}
          </div>
        </div>

        {selectedReport === 'pnl' ? (
          <div className="fin-report-paper__section">
            <h4 className="fin-report-paper__section-title">Estado de Ingresos y Gastos del Ejercicio</h4>
            <table className="fin-report-paper__table">
              <thead>
                <tr>
                  <th>Cuenta PGC / Partida</th>
                  <th>Denominación</th>
                  <th className="right">Importe (€)</th>
                  <th className="right">% s/ Venta</th>
                </tr>
              </thead>
              <tbody>
                {pnlRows.map((row, idx) => (
                  row.kind === 'result' ? (
                    <tr key={idx} className="result">
                      <td colSpan={2}>{row.name}</td>
                      <td className={`right ${row.tone}`}>{fmt(row.amount)}</td>
                      <td className="right">{row.pct}</td>
                    </tr>
                  ) : (
                    <tr key={idx} className={row.kind === 'total' ? 'total' : ''}>
                      <td className={row.kind === 'total' ? row.tone : 'indent'}>{row.code}</td>
                      <td className={row.kind === 'line' ? 'muted' : ''}>{row.name}</td>
                      <td className={`right ${row.kind === 'total' ? row.tone : ''}`}>{fmt(row.amount)}</td>
                      <td className="right">{row.pct}</td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        ) : isAmort ? (
          <div className="fin-report-paper__section">
            <h4 className="fin-report-paper__section-title">Amortizaciones · Área contable</h4>
            <table className="fin-report-paper__table fin-report-paper__table--stacked">
              <thead>
                <tr>
                  <th rowSpan={3}>Soc.</th>
                  <th rowSpan={3}>Cta. mayor</th>
                  <th rowSpan={3}>Denominación</th>
                  <th className="right">Val.adq.acum.</th>
                  <th className="right">Amo ac.</th>
                  <th className="right">Inicio val.cont.</th>
                  <th rowSpan={3}>Mon.</th>
                </tr>
                <tr>
                  <th className="right">Val.valor adq.</th>
                  <th className="right">Amo prev.</th>
                  <th />
                </tr>
                <tr>
                  <th className="right">Correcciones</th>
                  <th className="right">Val.Amo acumul.</th>
                  <th className="right">Val.neto final</th>
                </tr>
              </thead>
              <tbody>
                {amortLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <tr key={i} className="block-end">
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j}><Skeleton height="1.2em" /></td>
                      ))}
                    </tr>
                  ))
                ) : amortError || amortRows.length === 0 ? (
                  <tr className="block-end">
                    <td colSpan={7} className="muted">
                      {amortError || 'No hay activos fijos con valoración contable a esa fecha.'}
                    </td>
                  </tr>
                ) : (
                  <>
                    {amortRows.map((row, i) => renderAmortBlock(row, String(i)))}
                    {renderAmortBlock(amortTotal, 'total', true)}
                  </>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="fin-report-paper__section">
            <h4 className="fin-report-paper__section-title">{report?.name}</h4>
            <div className="fin-report-paper__summary">
              <div className="fin-report-paper__summary-row">
                <span className="strong">Estado de Cuentas:</span>
                <span className="income strong">✓ Auditado y Cuadrado</span>
              </div>
              {summaryRows.map(r => (
                <div key={r.label} className="fin-report-paper__summary-row">
                  <span>{r.label}:</span>
                  <strong className={r.label === 'Resultado Operativo Neto' ? (r.value >= 0 ? 'income' : 'expense') : ''}>
                    {fmt(r.value)}
                  </strong>
                </div>
              ))}
            </div>
            <p className="fin-report-paper__disclaimer">
              Este informe recoge fielmente los apuntes contables del Libro Diario en vigor de acuerdo con las normativas mercantiles y tributarias del Plan General Contable (PGC).
            </p>
          </div>
        )}

        <div className="fin-report-paper__footer">
          ALBATROSS CONTABILIDAD Y GESTIÓN FINANCIERA • DOCUMENTO GENERADO AUTOMÁTICAMENTE
        </div>
      </div>
    </div>
  );
};

export default FinanceReportsCenterView;
